import axios from 'axios';
import { API_BASE_URL } from '../config';
import dayjs from 'dayjs';
import { adminApi } from './adminApi';

const formatDate = (date, isEnd = false) => {
    if (!date) return null;
    let d = dayjs(date);
    if (!d.isValid()) return null;
    d = isEnd ? d.endOf('day') : d.startOf('day');
    return d.toISOString();
};

export const aggregateSalesFromOrders = (orders, startDate, endDate, selectedBrands = []) => {
    const startMs = dayjs(startDate).startOf('day').valueOf();
    const endMs = dayjs(endDate).endOf('day').valueOf();

    const brandMap = {};
    let totalSales = 0;
    let totalUnits = 0;
    let totalOrders = 0;

    const brandFilterLower = selectedBrands.map(b => b.toLowerCase().replace(/[-_]/g, ' '));

    (orders || []).forEach(order => {
        if (!order || !order.created_at) return;
        const orderStatus = (order.status || '').toLowerCase();
        if (['cancelled', 'refunded', 'failed'].includes(orderStatus)) return;

        const orderTime = dayjs(order.created_at).valueOf();
        if (orderTime < startMs || orderTime > endMs) return;

        totalOrders += 1;
        const dateStr = dayjs(order.created_at).format('YYYY-MM-DD');

        (order.items || []).forEach(item => {
            const rawBrand = item.brand || item.collection_handle || item.vendor || 'General';
            const brandKey = rawBrand;
            const brandKeyLower = rawBrand.toLowerCase().replace(/[-_]/g, ' ');

            if (brandFilterLower.length > 0 && !brandFilterLower.some(f => brandKeyLower.includes(f) || f.includes(brandKeyLower))) {
                return;
            }

            const price = Number(item.price || 0);
            const quantity = Number(item.quantity || 1);
            const itemTotal = price * quantity;

            totalSales += itemTotal;
            totalUnits += quantity;

            if (!brandMap[brandKey]) {
                brandMap[brandKey] = {
                    brand: brandKey,
                    total_sales: 0,
                    total_units: 0,
                    dailyMap: {}
                };
            }

            brandMap[brandKey].total_sales += itemTotal;
            brandMap[brandKey].total_units += quantity;

            if (!brandMap[brandKey].dailyMap[dateStr]) {
                brandMap[brandKey].dailyMap[dateStr] = { date: dateStr, amount: 0, units: 0 };
            }
            brandMap[brandKey].dailyMap[dateStr].amount += itemTotal;
            brandMap[brandKey].dailyMap[dateStr].units += quantity;
        });
    });

    const brands = Object.values(brandMap).map(b => ({
        brand: b.brand,
        total_sales: b.total_sales,
        total_units: b.total_units,
        daily_data: Object.values(b.dailyMap).sort((x, y) => x.date.localeCompare(y.date))
    }));

    return {
        status: 200,
        message: "Sales data computed from active orders",
        data: {
            summary: {
                total_sales: totalSales,
                total_orders: totalOrders,
                total_units: totalUnits
            },
            brands
        }
    };
};

export const salesService = {
    getSalesData: async (email, authKey, userId, brands = [], startDate, endDate) => {
        try {
            const finalAuthKey = authKey || localStorage.getItem('authKey') || 'admin';
            const finalUserId = userId || localStorage.getItem('userId') || 'admin';

            const formattedStartDate = formatDate(startDate, false);
            const formattedEndDate = formatDate(endDate, true);
            
            if (!formattedStartDate || !formattedEndDate) {
                throw new Error('Invalid date format');
            }
            const payload = {
                auth_key: finalAuthKey,
                user_id: finalUserId,
                brands: Array.isArray(brands) ? brands : [],
                start_date: formattedStartDate,
                end_date: formattedEndDate
            };

            let primaryData = null;
            try {
                const response = await axios.post(`${API_BASE_URL}/brands/sales`, payload);
                primaryData = response.data;
            } catch (err) {
                console.warn('Backend /brands/sales error:', err);
            }

            if (primaryData && primaryData.data && primaryData.data.summary) {
                if (Array.isArray(brands) && brands.length > 0) {
                    const brandFilterLower = brands.map(b => b.toLowerCase().replace(/[-_]/g, ' '));
                    const filteredBrands = (primaryData.data.brands || []).filter(b => {
                        const bName = (b.brand || '').toLowerCase().replace(/[-_]/g, ' ');
                        const bHandle = (b.handle || '').toLowerCase().replace(/[-_]/g, ' ');
                        return brandFilterLower.some(f => bName.includes(f) || f.includes(bName) || bHandle.includes(f) || f.includes(bHandle));
                    });

                    // If backend response included unselected brands, filter primaryData
                    if (filteredBrands.length < (primaryData.data.brands || []).length) {
                        let filteredSales = 0;
                        let filteredUnits = 0;
                        filteredBrands.forEach(b => {
                            filteredSales += Number(b.total_sales || 0);
                            filteredUnits += Number(b.total_units || 0);
                        });

                        return {
                            ...primaryData,
                            data: {
                                ...primaryData.data,
                                summary: {
                                    ...primaryData.data.summary,
                                    total_sales: filteredSales,
                                    total_units: filteredUnits,
                                },
                                brands: filteredBrands,
                                all_brands: primaryData.data.all_brands || primaryData.data.brands || []
                            }
                        };
                    }
                }
                return primaryData;
            }

            // Fallback: Compute sales directly from adminApi orders
            try {
                const ordersRes = await adminApi.getOrders({ limit: 500 });
                const orders = Array.isArray(ordersRes) ? ordersRes : (ordersRes.data || []);
                if (orders.length > 0) {
                    const computedData = aggregateSalesFromOrders(orders, startDate, endDate, brands);
                    if (computedData.data.summary.total_sales > 0) {
                        return computedData;
                    }
                }
            } catch (orderErr) {
                console.warn('Order aggregation fallback failed:', orderErr);
            }

            return primaryData || {
                status: 200,
                message: "Sales data fetched successfully",
                data: {
                    summary: { total_sales: 0, total_orders: 0, total_units: 0 },
                    brands: []
                }
            };
        } catch (error) {
            throw error.response?.data || error.message;
        }
    }
}; 